import { defaultTheme } from 'react-admin'

/**
 * Soft: A gentle theme for apps with rich content (images, charts, maps, etc).
 *
 * Uses white app bar, rounder corners, light colors.
 */

export const softDarkTheme = {
  palette: {
    primary: {
      main: '#90caf9',
    },
    secondary: {
      main: '#FBBA72',
    },
    mode: 'dark' as 'dark', // Switching the dark mode on is a single property value change.
  },
  sidebar: {
    width: 200,
  },
  components: {
    ...defaultTheme.components,
    RaMenuItemLink: {
      styleOverrides: {
        root: {
          borderLeft: '3px solid #000',
          '&.RaMenuItemLink-active': {
            borderLeft: '3px solid #90caf9',
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: undefined,
    },
    MuiAppBar: {
      styleOverrides: {
        colorSecondary: {
          color: '#ffffffb3',
          backgroundColor: '#616161',
        },
      },
      defaultProps: {
        elevation: 1,
      },
    },
  },
}

// Quiet, uniform column headers. They used to be bold purple (2025-07), which
// spent the one UI accent (UI-STYLE-GUIDE §2: nav, links, primary buttons,
// focus) on every table on every screen.
const TABLE_HEAD_CELL = {
  color: '#4b5563',
  fontWeight: 600,
  fontSize: '0.75rem',
  letterSpacing: '0.02em',
  backgroundColor: '#fafafb',
  '& .MuiTableSortLabel-root.Mui-active, & .MuiTableSortLabel-root:hover': { color: '#1f2937' },
}

export const softLightTheme = {
  palette: {
    primary: {
      main: '#4f3cc9',
    },
    secondary: {
      light: '#5f5fc4',
      main: '#283593',
      dark: '#001064',
      contrastText: '#fff',
    },
    background: {
      default: '#fcfcfe',
    },
    mode: 'light' as 'light',
  },
  shape: {
    borderRadius: 10,
  },
  typography: {
    // One heading scale for every page. Page titles use h5 (see PageHeader),
    // card/section titles h6, so no screen invents its own 34px/300-weight mix.
    h4: { fontWeight: 700, letterSpacing: '-0.01em' },
    h5: { fontWeight: 700, letterSpacing: '-0.01em' },
    h6: { fontWeight: 600, fontSize: '1.0625rem' },
    subtitle1: { fontWeight: 600 },
    button: { fontWeight: 600 },
  },
  sidebar: {
    width: 200,
  },
  components: {
    ...defaultTheme.components,
    // Sentence-case buttons everywhere. The portal mixed MUI's default
    // ALL-CAPS ("ADD FILTER", "HIDE FROM MY LIST") with hand-written
    // sentence-case ("Add Rule"); all-caps also wrapped per-row actions onto
    // three lines. Accessible names are unaffected (CSS-only).
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none' as const, borderRadius: 8 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none' as const, fontWeight: 600 },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 500 },
      },
    },
    // react-admin's default empty list: a 9em gray inbox glyph at 50% opacity
    // under a 34px headline, floating on the bare page. Restyled to match
    // components/EmptyState (quiet icon disc, one line, the next action) so
    // every empty list reads the same without touching each <List>.
    RaEmpty: {
      styleOverrides: {
        root: {
          display: 'block',
          backgroundColor: '#fff',
          border: '1px solid #e0e0e3',
          borderRadius: 10,
          padding: '48px 24px',
          '& .RaEmpty-message': { opacity: 1, color: '#6b7280', margin: 0 },
          '& .RaEmpty-icon': {
            width: 48,
            height: 48,
            padding: 12,
            borderRadius: '50%',
            backgroundColor: '#f3f4f6',
            color: '#6b7280',
            marginBottom: 16,
          },
          '& .MuiTypography-h4': {
            fontSize: '1rem',
            fontWeight: 600,
            color: '#111827',
            marginBottom: 4,
          },
          '& .MuiTypography-body1': { fontSize: '0.875rem' },
          '& .RaEmpty-toolbar': { marginTop: 20 },
        },
      },
    },
    // RaList-main / RaEdit-main are flex items with min-width:auto, so a wide
    // datagrid or form grew them to its min-content (545px on a 390 phone)
    // and the in-card table scroll above never engaged — content ran off the
    // clipped page instead. Let them shrink to the page.
    RaList: { styleOverrides: { root: { '& .RaList-main': { minWidth: 0 } } } },
    RaEdit: { styleOverrides: { root: { '& .RaEdit-main': { minWidth: 0 } } } },
    RaCreate: { styleOverrides: { root: { '& .RaCreate-main': { minWidth: 0 } } } },
    RaMenuItemLink: {
      styleOverrides: {
        root: {
          borderLeft: '3px solid #fff',
          '&.RaMenuItemLink-active': {
            borderLeft: '3px solid #4f3cc9',
            background:
              'linear-gradient(90deg, rgba(79, 60, 201, 0.1) 0%, rgba(79, 60, 201, 0.05) 100%)',
            '& .MuiListItemText-primary': {
              color: '#4f3cc9',
              fontWeight: 600,
            },
            '& .MuiSvgIcon-root': {
              color: '#4f3cc9',
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        elevation1: {
          boxShadow: 'none',
        },
        root: {
          border: '1px solid #e0e0e3',
          backgroundClip: 'padding-box',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        colorSecondary: {
          color: '#808080',
          backgroundColor: '#fff',
        },
      },
      defaultProps: {
        elevation: 1,
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: '#f5f5f5',
        },
        barColorPrimary: {
          backgroundColor: '#d7d7d7',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:last-child td': { border: 0 },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': TABLE_HEAD_CELL,
        },
      },
    },
    // React Admin Datagrid styling
    RaDatagrid: {
      styleOverrides: {
        root: {
          // A datagrid wider than its card scrolls INSIDE the card; the page
          // never scrolls sideways (see layout/Layout.tsx).
          '& .RaDatagrid-tableWrapper': { overflowX: 'auto' },
          // Per-row action buttons stay on one line ("Hide from my list" wrapped
          // to three); the wrapper above scrolls if the row gets wide.
          '& .RaDatagrid-rowCell .MuiButton-root': { whiteSpace: 'nowrap' },
          '& .RaDatagrid-headerCell': TABLE_HEAD_CELL,
        },
      },
    },
  },
}
